import Dialog from '@mui/material/Dialog';
import { Box, InputAdornment, TextField } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useState, useEffect } from 'react';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { Text } from '../../Atoms/Text';

export interface DialogParams {
  value?: number;
  open: boolean;
  setOpen: (state: boolean) => void;
  handleSubmit: (value: number) => Promise<boolean | undefined>;
}

export const RepeatingOverrideModal = ({ value, open, setOpen, handleSubmit }: DialogParams) => {
  const [tempValue, setTempValue] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    setTempValue(value ? String(value) : null);
    setOpen(false);
  };

  useEffect(() => {
    setTempValue(value ? String(value) : null);
  }, [value]);

  useEffect(() => {
    if (open) {
      setIsSaving(false);
    }
  }, [open]);

  const handleSubmitForm = () => {
    if (tempValue !== null) {
      const numericValue = Number(tempValue);
      if (!isNaN(numericValue)) {
        setIsSaving(true);
        handleSubmit(numericValue).finally(() => setIsSaving(false));
      }
    }
  };

  function cleanInput(input: string): string {
    let cleaned = input.replace(/[^0-9.]/g, '');

    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    if (cleaned.includes('.')) {
      const [whole, decimal] = cleaned.split('.');
      cleaned = `${whole || '0'}.${decimal.slice(0, 2)}`;
    }

    return cleaned;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    const cleaned = cleanInput(input);
    if (cleaned === '' && input !== '') return;
    setTempValue(cleaned);
  };

  return (
    <Dialog
      data-testid="repeating-override-modal"
      maxWidth="md"
      open={open}
      onClose={handleClose}>
      <DialogContent
        sx={{
          minHeight: 70,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'left',
          overflowY: 'visible'
        }}>
        <Box
          sx={{
            margin: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start'
          }}>
          <Text
            size="sm14"
            color="grey6"
            className="mb-2">
            Repeating Override
          </Text>
          <TextField
            value={tempValue}
            disabled={isSaving}
            onChange={handleInputChange}
            inputRef={(input) => input && input.focus()}
            data-testid="repeating-override-input"
            size="small"
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
          />
        </Box>
      </DialogContent>
      <hr className="mb-1 mx-6" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          disabled={isSaving}
          onClick={handleClose}>
          Cancel
        </button>
        <button
          onClick={() => handleSubmitForm()}
          disabled={!tempValue || isNaN(Number(tempValue)) || isSaving}
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          type="submit">
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
